import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
// iter353 — official green-and-orange W, embedded as a data URI so
// placeholders render instantly and even fully offline.
import { WASSLA_W_LOGO } from '../../assets/wasslaLogoData';
import '../../styles/wassla-image.css';

const W_LOGO_SRC = WASSLA_W_LOGO;

/**
 * iter327 — WasslaImage
 *
 * Canonical branded image loader for every ad/listing/user/category
 * thumbnail across Wassla. Owns a 3-state pipeline:
 *
 *   loading  → soft warm-white skeleton + diagonal shimmer + small
 *              centered orange Wassla "W" with a gentle pulse.
 *   loaded   → real image fades in over 200ms (per spec 180–250ms).
 *   error    → animations halt; mark dims to neutral grey; optional
 *              "Image unavailable" / "الصورة غير متاحة" caption.
 *
 * Key behaviours
 * --------------
 *  • Reserves space via `aspect-ratio` BEFORE the image lands, so no
 *    layout shift while a grid streams in.
 *  • 8s safety timeout (per spec) flips state to `error` if the CDN
 *    stalls. Cleared on unmount / state change.
 *  • lazy-loads everything by default; the `priority` prop opts an
 *    above-the-fold image into `fetchpriority="high"` + eager load.
 *  • `prefers-reduced-motion` halts shimmer + pulse via CSS; the
 *    mark stays visible (degrading to a brand-still).
 *
 * Props
 * -----
 *   src           string         — image URL. Empty/falsy → renders the
 *                                  error/fallback state immediately.
 *   alt           string         — accessible alt text.
 *   className     string?        — passed through to the shell.
 *   fallbackType  'ad' | 'book' | 'user' | 'category'  (default 'ad').
 *                                  Reserved for future per-type fallback
 *                                  artwork; for iter327 every type uses
 *                                  the same dim-grey mark + caption,
 *                                  matching the spec ("simple image icon").
 *   aspectRatio   string?        — CSS aspect-ratio value. Default '1 / 1'.
 *   objectFit     string?        — img object-fit. Default 'cover'.
 *   priority      bool?          — above-the-fold hint. Adds
 *                                  fetchpriority='high' + loading='eager'.
 *   timeoutMs     number?        — stall-timeout. Default 8000 (spec).
 *   showCaption   bool?          — render "Image unavailable" caption on
 *                                  error. Default true (per spec). Pass
 *                                  false for tiny thumbnails where the
 *                                  text would overflow.
 *   onLoad        fn?            — forwarded to the <img>.
 *   onError       fn?            — forwarded to the <img>.
 *   testid        string?        — data-testid. Default 'wassla-image'.
 *
 * Performance / hygiene
 * ---------------------
 *  • Single <img> in the DOM (opacity-toggled). No duplicate hidden
 *    elements.
 *  • Timeout cleanup wired in useEffect return so unmounting a card
 *    mid-load doesn't leak setTimeouts.
 *  • All styling lives in /app/frontend/src/styles/wassla-image.css —
 *    no inline keyframes here, no per-instance recalc cost.
 */
export default function WasslaImage({
  src,
  alt = '',
  className = '',
  // Reserved-for-future: per-type fallback artwork. Currently only used
  // as a data-attribute so future themes / tests can target it.
  fallbackType = 'ad',
  aspectRatio = '1 / 1',
  objectFit = 'cover',
  priority = false,
  timeoutMs = 8000,
  showCaption = true,
  onLoad,
  onError,
  testid = 'wassla-image',
}) {
  const { t } = useTranslation();

  // Initial state: 'loading' if we have a URL, else jump straight to
  // 'error' so the buyer never sees a 1-frame broken-img icon on
  // Safari (which paints <img src=""> even for empty srcs).
  const [state, setState] = useState(src ? 'loading' : 'error');
  // iter353 — retry counter. Bumped by the retry button on error; the
  // img src gets a cache-busting param so the browser re-fetches.
  const [retry, setRetry] = useState(0);

  // Reset the state machine if the src prop changes at runtime
  // (e.g. user navigates between ads in a modal).
  const lastSrcRef = useRef(src);
  useEffect(() => {
    if (src !== lastSrcRef.current) {
      lastSrcRef.current = src;
      setRetry(0);
      setState(src ? 'loading' : 'error');
    }
  }, [src]);

  // 8-second safety timeout. Spec requirement #4 + #9 — "do not keep
  // infinite spinner forever". On stall we flip to error so the
  // fallback paints and the buyer knows the image isn't coming.
  useEffect(() => {
    if (state !== 'loading') return undefined;
    const id = setTimeout(() => setState('error'), timeoutMs);
    return () => clearTimeout(id);
  }, [state, timeoutMs]);

  const handleLoad = (e) => {
    setState('loaded');
    if (typeof onLoad === 'function') onLoad(e);
  };
  const handleError = (e) => {
    setState('error');
    if (typeof onError === 'function') onError(e);
  };
  const handleRetry = (e) => {
    // Cards are clickable — never let a retry tap open the ad.
    e.stopPropagation();
    e.preventDefault();
    setRetry((r) => r + 1);
    setState('loading');
  };

  // Cache-busted URL on retry so a failed CDN response isn't replayed
  // from the browser cache.
  const effectiveSrc = src && retry > 0
    ? `${src}${src.includes('?') ? '&' : '?'}wretry=${retry}`
    : src;

  // Shell uses inline style for the aspect-ratio so callers can
  // override per-instance (e.g. seller cover banners use 16/9).
  // Everything else flows through CSS classes.
  const shellStyle = { aspectRatio };
  const imgStyle = { objectFit };

  // Caption falls back to EN if the AR key isn't loaded yet. i18n
  // returns the key name on miss; we guard for that so users never
  // see "image.unavailable" raw text.
  const captionKey = 'image.unavailable';
  const captionText = (() => {
    const raw = t(captionKey);
    if (raw && raw !== captionKey) return raw;
    return 'Image unavailable';
  })();

  return (
    <span
      className={`wassla-image-shell ${className}`.trim()}
      data-state={state}
      data-fallback-type={fallbackType}
      data-testid={testid}
      style={shellStyle}
      aria-busy={state === 'loading'}
    >
      {/* Skeleton + branded mark. CSS hides this layer when
          state === 'loaded'; we always mount it (no React churn)
          so slow phones don't repaint the structure twice. */}
      <span className="wassla-image-placeholder" aria-hidden="true">
        <span className="wassla-image-shimmer" />
        {/* iter353 — official W logo for BOTH the loading state and the
            broken-image fallback. Static (no pulse/glow per spec). */}
        <span className="wassla-image-w">
          <img
            src={W_LOGO_SRC}
            alt=""
            className="wassla-image-w-img"
            draggable={false}
            loading="lazy"
            decoding="async"
          />
        </span>
      </span>

      {/* Retry affordance on failure (spec: keep logo + retry mechanism). */}
      {state === 'error' && src && (
        <button
          type="button"
          className="wassla-image-retry"
          onClick={handleRetry}
          aria-label="Retry loading image"
          data-testid={`${testid}-retry`}
        >
          <RefreshCw size={13} />
        </button>
      )}

      {showCaption && state === 'error' && (
        <span
          className="wassla-image-fallback"
          data-testid={`${testid}-fallback-caption`}
        >
          {captionText}
        </span>
      )}

      {/* Only mount the <img> when there's a URL — otherwise Safari
          paints a "broken image" glyph for empty src. */}
      {src && state !== 'error' && (
        <img
          key={retry}
          className="wassla-image-img"
          src={effectiveSrc}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : undefined}
          onLoad={handleLoad}
          onError={handleError}
          style={imgStyle}
        />
      )}
    </span>
  );
}
